import { useEffect, useState } from 'preact/hooks'

import { formatMoney, globalState } from 'helpers/utils'

import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'
import useURL from 'hooks/useUrl'
import { useGwp } from 'hooks/useGwp'

import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { createPortal } from 'preact/compat'
import clsx from 'clsx'
import { GwpCartBlock, GwpGiftItem } from './gwp-cart-block'

/**
 * Extract colour code from product tags and find matching swatch
 * @param {string[]} tags - Array of product tags
 * @returns {object|null} - Matching swatch object or null
 */
const getSwatchFromTags = (tags) => {
  if (!tags || !Array.isArray(tags)) return null

  const colourCodeTag = tags.find(tag => tag.startsWith('x-colourCode:'))
  if (!colourCodeTag) return null

  const colourCode = colourCodeTag.split(':')[1]
  if (!colourCode) return null
  const swatches = globalState?.metaobjects?.swatches || []
  return swatches.find(swatch =>
    swatch.tags && swatch.tags.includes(colourCodeTag)
  ) || null
}

const actionKeys = {
  PLUS: 'plus',
  MINUS: 'minus',
  REMOVE: 'remove'
}

const onChangeActionValue = (key, quantity) => {
  return {
    [actionKeys.PLUS]: quantity + 1,
    [actionKeys.MINUS]: quantity - 1,
    [actionKeys.REMOVE]: 0
  }[key]
}

const OnChangeTrigger = ({ type, value, className, p }) => {
  return (
    <div class={`group relative shrink-0 cursor-pointer ${p}`}>
      <input
        type="checkbox"
        data-action={type}
        class=" absolute z-2 size-full cursor-pointer appearance-none border-0 bg-transparent opacity-0 inset-tl"
        aria-label={value}
      />
      <div class={className}>{value}</div>
    </div>
  )
}

/**
 *
 * Handles line item quantity update.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig }) => {
  const plusIcon = <Icon id="plus" />
  const minusIcon = <Icon id="minus" />
  const trashIcon = <Icon id="trash" className="h-[15px] w-[15px]" />
  const [error, setError] = useState(null)

  /** Checks if the current Line Item new quantity is above the available inventory  */
  const handleLineItemQuantityChange = (item, newQuantity) => {
    if (newQuantity > item.inventory_quantity) {
      setError('Maximum quantity reached')
      setTimeout(() => setError(null), 5000)
      throw new Error('Unavailable product stock quantity')
    }
    setError(null)
  }

  const onLineItemQuantityChange = async (e) => {
    try {
      e.preventDefault()
      /** Get Action Key */
      const action = e.target.dataset.action
      /** Set MiniCart state to isLoading true */
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      /** Get new line item quantity based off action */
      const newQuantity = onChangeActionValue(action, item.quantity)
      /** Throw an error is the new quantity is over the available stock */
      handleLineItemQuantityChange(item, newQuantity)
      /** Update line item quantity */
      await window.Cart.updateLineItemQuantity(
        {
          id: item.variant_id,
          quantity: newQuantity
        },
        { silent: false }
      )

      /** Poll Shopify for new Cart item and set cart config state */
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  return (
    <>
      <form onChange={onLineItemQuantityChange} class="flex items-center justify-between gap-x-0.5 md:gap-x-1">
        <div class="flex shrink-0 border border-neutral-medium">
          <OnChangeTrigger
            p="btn btn-primary btn-transparent btn-square w-[36px] !border-0"
            type={actionKeys.MINUS}
            value={minusIcon}
            className={'size-fit children:h-[2px] children:w-[14px]'}
          />
          <div class="flex w-2 grow items-center justify-center text-center text-s">{item.quantity}</div>
          <OnChangeTrigger
            p="btn btn-primary btn-transparent btn-square w-[36px] !border-0"
            type={actionKeys.PLUS}
            value={plusIcon}
            className={'size-fit children:size-[14px]'}
          />
        </div>

        <OnChangeTrigger
          p="btn btn-primary btn-text btn-s text-xs"
          type={actionKeys.REMOVE}
          value={trashIcon}
          className={''}
        />
      </form>
      <p class={clsx('paragraph-xs hidden w-full text-system-error transition-all', error && '!flex')}>{error}</p>
    </>
  )
}

/**
 *
 *
 * Mini Cart Snippet,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet ({ cartSubTotal, freeShippingGoal }) {
  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }

  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  const reachedGoal = freeShippingMinimum - cartSubTotal
  const message =
    reachedGoal <= 0 ? (
      <>
        <p class="overflow-hidden paragraph-s lg:paragraph-m text-ellipsis whitespace-nowrap">
          You're receiving free standard shipping
        </p>
      </>
    ) : (
      <>
        <p class="overflow-hidden paragraph-s lg:paragraph-m text-ellipsis whitespace-nowrap">
          You're <span class="font-medium">{formatMoney(reachedGoal)}</span> away from free standard shipping
        </p>
      </>
    )
  return (
    <div
      data-role="free_shipping_monitor"
      class={clsx(
        'flex flex-col gap-0.5 border-t border-t-neutral-medium px-1 py-1 transition-all md:px-1.5',
        reachedGoal <= 0 ? 'bg-system-success-light' : 'border-b border-b-neutral-medium bg-neutral-light'
      )}
    >
      <div class="flex items-center justify-center gap-x-0.375 text-center">{message}</div>
    </div>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const DiscountedPrice = ({ newPrice, oldPrice }) => {
    return (
      <div class="flex flex-col items-end text-right">
        <p class={`paragraph-xs lg:paragraph-s font-medium ${newPrice === 0 ? 'text-system-success' : 'text-brand-sale'}`}>
          {newPrice === 0 ? 'Free' : formatMoney(newPrice)}
        </p>
        <p class="paragraph-xs lg:paragraph-s text-neutral-primary line-through">{formatMoney(oldPrice)}</p>
      </div>
    )
  }

  if (item.discounts.length > 0 && item.original_line_price !== item.line_price) {
    return <DiscountedPrice newPrice={item.line_price} oldPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <DiscountedPrice newPrice={item.line_price} oldPrice={item.compare_at_price * item.quantity} />
  }
  return (
    <div class="flex flex-col items-end text-right">
      <p class={`paragraph-xs lg:paragraph-s font-medium ${item.original_line_price === 0 ? 'text-system-success' : ''}`}>
        {item.original_line_price === 0 ? 'Free' : formatMoney(item.original_line_price)}
      </p>
    </div>
  )
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts.map((discount) => {
        return (
          <div class="paragraph-xs text-neutral-primary flex flex-row items-center justify-start uppercase">
            <Icon id="tag" className="mr-[5px] h-[14px] w-[14px]" />
            <span>{discount.title}</span>
          </div>
        )
      })}
    </>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath }) => {
  return (
    <a href={productPath} class="link-fade" aria-label="Item image">
      <PreactImage
        className="flex h-full aspect-2/3 w-[104px] shrink flex-grow children:size-full children:object-cover lg:w-[126px]"
        key={id}
        imageUrl={[imageSrc]}
      />
    </a>
  )
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  let colourName = ''
  const swatch = getSwatchFromTags(item.tags)
  if (swatch) {
    colourName = swatch.display_name || ''
  } else if (item.product_colour_name !== '') {
    colourName = item.product_colour_name
  }
  const itemTitle = item.customer_facing_product_name || item.product_title
  return (
    <div class="flex flex-row justify-between gap-x-0.5 md:gap-x-1">
      <div class="flex flex-col gap-y-0.5">
        <a class="paragraph-s text-brand-black line-clamp-1">{itemTitle}</a>
        <p class="paragraph-xs text-neutral-primary">
          {colourName && (
            <span class="capitalize">{colourName}<span className="px-0.5"> | </span></span>
          )}
          {item.options_with_values.map((option, index) => (
            <span>
              {option.value}
              {index < item.options_with_values.length - 1 && <span className="px-0.5"> | </span>}
            </span>
          ))}
        </p>
      </div>
      <ItemPrice item={item} />
    </div>
  )
}

const MiniCartLineItem = ({ item, gift, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading && 'opacity-50'

  return (
    <div class={`flex flex-col border-b border-neutral-medium transition-all ${loadingState}`}>
      <div class="flex flex-row gap-x-0.5 md:gap-x-1">
        <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} />
        <div class="flex w-full flex-grow flex-col gap-y-1 py-1.5 pr-1">
          <MiniCartItemDetails item={item} />
          <MiniCartItemDiscount item={item} />
          <MiniCartItemQuantity item={item} handleSetCartConfig={handleSetCartConfig} />
        </div>
      </div>
      {gift && (
        <GwpGiftItem
          gift={gift}
          onAddToCart={({ loading, cart: newCart }) => {
            if (loading !== undefined) handleSetCartConfig(CartConfigKeys.isLoading, loading)
            if (newCart) handleSetCartConfig(CartConfigKeys.cart, newCart)
          }}
          isLoading={isLoading}
        />
      )}
    </div>
  )
}

/**
 * Calculates total savings from compare_at_price discounts
 * @param {Array} cartItems - Array of cart items
 * @returns {number} - Total compare_at_price savings in cents
 */
const calculateCompareAtSavings = (cartItems) => {
  let compareAtSavings = 0

  for (const item of cartItems) {
    const linePricePerItem = item.line_price / item.quantity
    const compareAtPrice = item.compare_at_price

    if (compareAtPrice && compareAtPrice > linePricePerItem) {
      const savingsPerItem = compareAtPrice - linePricePerItem
      const itemSavings = savingsPerItem * item.quantity
      compareAtSavings += itemSavings
    }
  }

  return compareAtSavings
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, totalCartDiscount, isLoading, cart, discountText, totalText }) => {
  const cartTotal = formatMoney(total)
  const compareAtSavings = calculateCompareAtSavings(cart.items)
  const totalDiscountAmount = totalCartDiscount + compareAtSavings
  const totalDiscount = formatMoney(totalDiscountAmount)
  const subtotal = formatMoney(total + totalDiscountAmount)

  return (
    <div class="flex flex-col pt-0.5">
      <x-modal data-id="total-order-summary" class="group flex w-full flex-col transition-all data-visible:pb-1">
        <div class="flex max-h-[150vh] flex-col pt-0.625 transition-all">
          <div class={`flex flex-col gap-y-1 transition-all ${isLoading && 'opacity-50'}`}>
            <div class="flex justify-between gap-x-1">
              <p class="paragraph-s">Subtotal</p>
              <p class={`paragraph-s transition-all ${isLoading && 'opacity-50'}`}>{subtotal}</p>
            </div>
            {totalDiscountAmount > 0 && (
              <div className="flex justify-between gap-x-1">
                <p className="paragraph-s">{discountText || 'Discounts'}</p>
                <p className={`paragraph-s transition-all ${isLoading && 'opacity-50'}`}>-{totalDiscount}</p>
              </div>
            )}
          </div>
        </div>
      </x-modal>
      <div class="flex justify-between gap-x-1 py-1.5">
        <p class="paragraph-l font-medium">{totalText || 'Total'}</p>
        <p class={`paragraph-l font-medium transition-all ${isLoading && 'opacity-50'}`}>{cartTotal}</p>
      </div>
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ isLoading }) => {
  const { title, buttons } = window.minicart
  return (
    <>
      <div
        class={`flex grow flex-col items-center justify-center gap-y-1 px-margin py-6.25 text-center transition-all md:gap-y-1.5 ${
          isLoading ? 'opacity-0' : ''
        }`}
      >
        <h3 class={'h3'}>{title}</h3>
        <div
          class="flex flex-row flex-wrap gap-0.5 items-center justify-center children:w-[calc(50%-8px)] children:max-w-[calc(50%-8px)]"
          dangerouslySetInnerHTML={{ __html: buttons }}
        ></div>
      </div>
    </>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div>
      <a href="/checkout" class="btn w-full btn-fill btn-primary btn-l btn-bold" aria-label={checkoutButtonTitle}>
        {checkoutButtonTitle}
      </a>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, giftsWithoutThreshold, handleSetCartConfig, isLoading }) => {
  const giftsDisplayed = []
  return (
    <div class="flex grow flex-col" data-role="cart_items">
      {cart.items.map((item) => {
        const giftToDisplay =
          giftsWithoutThreshold?.find((gift) => {
            if (!gift.cartQualifies || gift.giftInCart || giftsDisplayed.includes(gift.id)) {
              return false
            }
            switch (gift.conditionalType) {
              case 'all':
                return true
              case 'products':
                return gift.conditionalIds.some((id) => id.split('/').pop() === item.product_id.toString())
              case 'collections':
                return item.collections?.some((collection) =>
                  gift.conditionalIds.some((id) => id.split('/').pop() === collection.id.toString())
                )
              case 'tags':
                return item.tags?.some((tag) => gift.conditionalTags.includes(tag))
              default:
                return false
            }
          }) ?? null
        if (giftToDisplay) {
          giftsDisplayed.push(giftToDisplay.id)
        }
        return (
          <MiniCartLineItem
            item={{ ...item }}
            gift={giftToDisplay}
            handleSetCartConfig={handleSetCartConfig}
            isLoading={isLoading}
          />
        )
      })}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart () {
  const path = useURL()
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum
  const { giftsWithoutThreshold } = useGwp(cart, freeShippingMinimum)

  const { checkoutButtonTitle, discountText, totalText } = globalCart

  useEffect(() => {
    const altPaymentButtons = document.querySelector('div.additional-checkout-buttons--vertical')
    if (!altPaymentButtons) return
    if (cart.items.length > 0) {
      altPaymentButtons.classList.remove('hidden')
    } else if (cart.items.length <= 0) {
      altPaymentButtons.classList.add('hidden')
    }
  }, [cartConfig])

  useEffect(() => {
    if (path.includes('timestamp=')) {
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      window.Cart.getCart().then(() => {
        handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }
  }, [path])

  return (
    <>
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-gwp">
            <GwpCartBlock
              cart={cart}
              freeShippingMinimum={freeShippingMinimum}
              enableProgressBar={globalCart.gwp?.enableProgressBar}
              handleSetCartConfig={handleSetCartConfig}
              isLoading={isLoading}
            />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems
              cart={cart}
              giftsWithoutThreshold={giftsWithoutThreshold}
              isLoading={isLoading}
              handleSetCartConfig={handleSetCartConfig}
            />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-shipping">
            <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary">
            <div className="flex flex-col">
              <DisplayCartTotal
                totalCartDiscount={cart.total_discount}
                total={cart.total_price}
                isLoading={isLoading}
                cart={cart}
                discountText={discountText}
                totalText={totalText}
              />
              <CheckoutButton checkoutButtonTitle={checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart
            cartConfig={cartConfig}
            globalCart={globalCart}
            isLoading={isLoading}
          />
        </MiniCartPortal>
      )}
    </>
  )
}

const MiniCartPortal = ({ children, targetId }) => {
  const target = document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}
